import type { AccentHue } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import {
  createMemo,
  createSignal,
  type JSX,
  type ParentProps,
  Show,
  splitProps,
} from "solid-js"
import { Dynamic } from "solid-js/web"
import { boxClass, buttonRecipe, iconRecipe } from "./button.css"
import { Icon } from "./icon"

type Tag = "button" | "a" | "label"
type GenericButtonProps = {
  hue: AccentHue
  kind?: "solid" | "soft" | "surface" | "ghost"
  size?: "s" | "m" | "readable"
  iconName?: IconName
  active?: boolean
  class?: string
  disabled?: boolean
} & ParentProps

type NativeProps =
  | JSX.ButtonHTMLAttributes<HTMLButtonElement>
  | JSX.AnchorHTMLAttributes<HTMLAnchorElement>
  | JSX.LabelHTMLAttributes<HTMLLabelElement>

function GenericButton(
  props: GenericButtonProps & { tag: Tag } & Partial<NativeProps>,
) {
  const [cProps, nProps] = splitProps(props, [
    "hue",
    "kind",
    "size",
    "iconName",
    "active",
    "children",
    "class",
    "tag",
    "disabled",
  ])
  const className = createMemo(() =>
    [
      buttonRecipe({
        hue: cProps.hue,
        kind: cProps.kind,
        size: cProps.size,
        active: !!cProps.active,
        disabled: !!cProps.disabled,
      }),
      cProps.class,
    ]
      .filter(Boolean)
      .join(" "),
  )

  return (
    <Dynamic component={cProps.tag} {...nProps} class={className()}>
      <div class={boxClass({ active: !!cProps.active })}>
        <Show when={cProps.iconName}>
          {iconName => (
            <Icon
              name={iconName()}
              class={iconRecipe({ hue: cProps.hue })}
              size={cProps.size === "s" ? "12" : "18"}
            />
          )}
        </Show>
        {cProps.children}
      </div>
    </Dynamic>
  )
}

type LinkButtonProps = JSX.AnchorHTMLAttributes<HTMLAnchorElement> &
  GenericButtonProps
export function LinkButton(props: LinkButtonProps) {
  return <GenericButton {...props} tag={"a" as Tag} />
}

type ButtonProps = JSX.ButtonHTMLAttributes<HTMLButtonElement> &
  GenericButtonProps
export function Button(props: ButtonProps) {
  const [isPromisePending, setIsPromisePending] = createSignal(false)
  const [local, rest] = splitProps(props, ["onClick"])

  const isActive = createMemo(() => !!props.active || isPromisePending())
  const disabled = createMemo(() => !!props.disabled || isActive())

  const handleClick: JSX.EventHandler<
    HTMLButtonElement,
    MouseEvent
  > = event => {
    if (!local.onClick || typeof local.onClick !== "function") return

    const result = local.onClick(event) as unknown

    if (result instanceof Promise) {
      setIsPromisePending(true)
      result.finally(() => setIsPromisePending(false))
    }

    return result
  }

  return (
    <GenericButton
      {...rest}
      active={isActive()}
      disabled={disabled()}
      onClick={handleClick}
      tag={"button" as Tag}
    />
  )
}
